import { Braces } from "lucide-react";
import * as React from "react";
import { useForm, type UseFormRegisterReturn } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AudioPicker } from "@/features/audio-picker";
import { DraftPreview } from "@/features/greeting-preview";
import { ApiError } from "@/lib/api";
import { isCurrentStage, stageSlugs, stageText } from "@/lib/contacts";
import { useAuth } from "@/lib/auth";
import { useFormChanged, zodResolver } from "@/lib/form";
import {
  useCreateVoicemailMessage, useGreetingFields, useTenants, useUpdateVoicemailMessage,
} from "@/lib/queries";
import type { GreetingField, VoicemailMessage } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  NAME_MAX, NEW_VOICEMAIL_MESSAGE, SCRIPT_MAX, VOICEMAIL_LANGUAGES, VOICEMAIL_SLUG_RE, insertField,
  languageProblem, languageText, newVoicemailMessagePayload, scriptProblem, versionBProblem,
  voicemailMediaProblem, voicemailMessageChanges, voicemailMessageValues, type VoicemailKind,
} from "@/lib/voicemail";

/**
 * Add a message to the voicemail library, or edit one (ADR-019, ADR-112 in `platform`).
 *
 * A message is a greeting written as text, with fields that are filled for each lead, or a
 * recording. The kind is chosen once, on a new message: a message keeps it, as it keeps its slug,
 * which a rep's drop and a cadence name it by. A recording is a file chosen from the audio store
 * (`AudioPicker`), and a script is typed here, with its fields put in from a menu so nobody types a
 * field's name (`insertField`). Neither is saved empty, because a message has no default to fall
 * back on. A script is read for a lead before it is saved (`DraftPreview`).
 *
 * A script can have a version B (ADR-116): a second text, for a lead nobody has reached for a day.
 * It is written under the greeting, the same way, and it is the one part of a script that may stay
 * empty. core-api decides which of the two a call gets; the form only writes them, and reads
 * either one for a lead.
 *
 * An edit sends only what changed (`voicemailMessageChanges`). Retiring is not on the form either:
 * it is the list's own action, with a confirmation of its own, because it takes the message off
 * every rep's phone.
 */

/** Radix's Select cannot hold `""`, so "no language" needs a value of its own. */
const NONE = "none";

const KIND_TEXT: Record<VoicemailKind, { label: string; says: string }> = {
  script: {
    label: "Greeting text",
    says: "Written here, with the lead's name and other fields filled in for each lead.",
  },
  recording: { label: "Recording", says: "A file from the audio store. Every lead hears the same words." },
};

/** `known` is the fields a greeting may use, once they are read. */
function schemaFor(known: ReadonlySet<string> | undefined) {
  return z
    .object({
      slug: z
        .string()
        .min(1, "Required")
        .regex(VOICEMAIL_SLUG_RE, "Lowercase letters, digits, underscore and hyphen, up to 63"),
      name: z
        .string()
        .trim()
        .min(1, "Required")
        .max(NAME_MAX, `At most ${NAME_MAX} characters`),
      kind: z.enum(["script", "recording"]),
      language: z.string(),
      stages: z.array(z.string()),
      media: z.string(),
      script: z.string(),
      script_b: z.string(),
      tenant_id: z.number().nullable(),
    })
    .superRefine((values, ctx) => {
      // Only the content of its kind is judged: a recording's empty script is not a mistake.
      if (values.kind === "script") {
        const problem = scriptProblem(values.script, known);
        if (problem) ctx.addIssue({ code: "custom", path: ["script"], message: problem });
        const second = versionBProblem(values.script_b, known);
        if (second) ctx.addIssue({ code: "custom", path: ["script_b"], message: second });
      } else {
        const problem = voicemailMediaProblem(values.media);
        if (problem) ctx.addIssue({ code: "custom", path: ["media"], message: problem });
      }
      const language = languageProblem(values);
      if (language) ctx.addIssue({ code: "custom", path: ["language"], message: language });
    });
}
type FormValues = z.infer<ReturnType<typeof schemaFor>>;

const BLANK: FormValues = { ...NEW_VOICEMAIL_MESSAGE, tenant_id: null };

export function VoicemailMessageFormDialog({
  open, onOpenChange, message,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  message?: VoicemailMessage;
}) {
  const { user: actor } = useAuth();
  const toast = useToast();
  const create = useCreateVoicemailMessage();
  const update = useUpdateVoicemailMessage();
  // Only a superadmin names a tenant, and only while there are several: with one, core-api fills it
  // in, and everyone else's is their own whatever they send.
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({ enabled: open && isSuperadmin && !message });
  const pickTenant = isSuperadmin && !message && (tenants?.length ?? 0) > 1;
  const fields = useGreetingFields(open);
  const known = React.useMemo(
    () => (fields.data ? new Set(fields.data.map((f) => f.name)) : undefined),
    [fields.data],
  );
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schemaFor(known)), defaultValues: BLANK });
  const { errors, isSubmitted } = form.formState;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(message ? { ...voicemailMessageValues(message), tenant_id: message.tenant_id } : BLANK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, message]);

  const values = form.watch();
  // A pick is judged once the form has been sent back with a mistake, not while it is being filled
  // in: a field put into an empty greeting is not yet a greeting "with no words of its own".
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    form.setValue(key, value as never, { shouldDirty: true, shouldValidate: isSubmitted });
  const isScript = values.kind === "script";

  const hasB = values.script_b.trim() !== "";
  // Which of the two texts the preview reads. With no version B there is one to read.
  const [reading, setReading] = React.useState<"a" | "b">("a");
  const read = hasB ? reading : "a";

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    if (pickTenant && submitted.tenant_id === null) {
      form.setError("tenant_id", { message: "Choose the tenant whose library it joins" });
      return;
    }
    try {
      if (message) {
        const changes = voicemailMessageChanges(submitted, message);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ id: message.id, ...changes });
        }
        toast.success("Message saved", submitted.name);
      } else {
        await create.mutateAsync({
          ...newVoicemailMessagePayload(submitted),
          ...(isSuperadmin && submitted.tenant_id !== null ? { tenant_id: submitted.tenant_id } : {}),
        });
        toast.success("Message added", submitted.name);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the message.");
    }
  });

  const knownLanguage = (VOICEMAIL_LANGUAGES as readonly string[]).includes(values.language);
  const stageOptions = [
    ...stageSlugs().map((code) => ({ value: code, label: stageText(code).label })),
    // A stage the tenant no longer has stays listed while the message still names it.
    ...values.stages.filter((s) => !isCurrentStage(s)).map((s) => ({ value: s, label: stageText(s).label })),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{message ? "Edit voicemail message" : "New voicemail message"}</DialogTitle>
          <DialogDescription>
            {message
              ? isScript
                ? "What it is called, what it says, and who hears it."
                : "What reps see it called, and what it plays."
              : "What a lead's voicemail gets when a dialer call reaches it: a greeting written as text, or a recording."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            {!message && (
              <div className="space-y-1.5">
                <Tabs value={values.kind} onValueChange={(v) => set("kind", v as VoicemailKind)}>
                  <TabsList aria-label="What the message is">
                    {(Object.keys(KIND_TEXT) as VoicemailKind[]).map((kind) => (
                      <TabsTrigger key={kind} value={kind}>
                        {KIND_TEXT[kind].label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <p className="text-xs text-text-muted">{KIND_TEXT[values.kind].says}</p>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="voicemail-name" required error={errors.name?.message}>
                <Input
                  autoComplete="off"
                  placeholder={isScript ? "Missed callback, friendly" : "Please call us back"}
                  invalid={Boolean(errors.name)}
                  {...form.register("name")}
                />
              </Field>
              <Field
                label="Slug"
                htmlFor="voicemail-slug"
                required={!message}
                error={errors.slug?.message}
                hint={
                  message
                    ? "Fixed. A drop names the message by it."
                    : "What a drop names the message by. It cannot be changed later."
                }
              >
                <Input
                  // Read-only rather than disabled: a disabled field drops out of the submitted values.
                  readOnly={Boolean(message)}
                  className={cn("font-mono", message && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder={isScript ? "missed_callback_en" : "callback_en"}
                  invalid={Boolean(errors.slug)}
                  {...form.register("slug")}
                />
              </Field>
            </div>

            {pickTenant && (
              <Field
                label="Tenant"
                htmlFor="voicemail-tenant"
                required
                error={errors.tenant_id?.message}
                hint="Each tenant has a library of its own, and its messages play files from its own audio store."
              >
                <Select
                  value={values.tenant_id?.toString() ?? ""}
                  onValueChange={(v) => set("tenant_id", Number(v))}
                >
                  <SelectTrigger id="voicemail-tenant" invalid={Boolean(errors.tenant_id)}>
                    <SelectValue placeholder="Choose a tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {(tenants ?? []).map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {isScript ? (
              <>
                <GreetingText
                  id="voicemail-script"
                  label={hasB ? "Greeting, version A" : "Greeting"}
                  required
                  error={errors.script?.message}
                  hint={
                    <>
                      A field is filled in for each lead. For a lead with no value, add words after a bar:{" "}
                      <span className="font-mono">{"{{lead.first_name|there}}"}</span>. With no such words, a
                      lead who lacks the value does not get this greeting.
                    </>
                  }
                  placeholder="Hi {{lead.first_name|there}}, this is {{rep.first_name}}…"
                  rows={7}
                  value={values.script}
                  onChange={(next) => set("script", next)}
                  registration={form.register("script")}
                  fields={fields.data}
                />
                <GreetingText
                  id="voicemail-script-b"
                  label="Version B"
                  error={errors.script_b?.message}
                  hint="Optional. What a lead hears instead once nobody has reached them for a day, counted from our first call that reached nobody since they entered their stage or last spoke to a rep. Left empty, they keep hearing the greeting above."
                  placeholder="Hi {{lead.first_name|there}}, this is {{rep.first_name}} again…"
                  rows={5}
                  value={values.script_b}
                  onChange={(next) => set("script_b", next)}
                  registration={form.register("script_b")}
                  fields={fields.data}
                />
              </>
            ) : (
              <Field
                label="Recording"
                htmlFor="voicemail-media"
                required
                error={errors.media?.message}
                hint="What plays to the lead's voicemail: a file from the tenant's audio store."
              >
                <AudioPicker
                  value={values.media}
                  onChange={(v) => set("media", v)}
                  emptyLabel="No recording chosen"
                />
              </Field>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Language"
                htmlFor="voicemail-language"
                required={isScript}
                error={errors.language?.message}
                hint={
                  isScript
                    ? "A lead hears the greeting in their own language, and its dates and times are said in it."
                    : "Shown beside the message on a rep's phone, so they leave the one in the lead's language."
                }
              >
                <Select
                  value={values.language || NONE}
                  onValueChange={(v) => set("language", v === NONE ? "" : v)}
                >
                  <SelectTrigger id="voicemail-language" invalid={Boolean(errors.language)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{isScript ? "Choose one" : "Not set"}</SelectItem>
                    {VOICEMAIL_LANGUAGES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {languageText(code)}
                      </SelectItem>
                    ))}
                    {values.language && !knownLanguage && (
                      <SelectItem value={values.language}>{languageText(values.language)}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>

              <Field
                label="Who it is for"
                htmlFor="voicemail-stages"
                hint="The stages of the leads who get it. Leave it on Everyone for the message a lead gets when no other is for their stage."
              >
                <div className="space-y-1.5">
                  <MultiSelect
                    label="Stages"
                    options={stageOptions}
                    value={values.stages}
                    onChange={(next) => set("stages", next)}
                    placeholder="Everyone"
                    className="max-w-full"
                  />
                  {values.stages.length > 1 && (
                    <p className="flex flex-wrap gap-1">
                      {values.stages.map((s) => (
                        <Badge key={s} tone="outline" size="sm">
                          {stageText(s).label}
                        </Badge>
                      ))}
                    </p>
                  )}
                </div>
              </Field>
            </div>

            {isScript && (
              <Field
                label="Preview for a lead"
                htmlFor="voicemail-preview-lead"
                hint="Pick a lead to read the greeting with their details. Nothing is saved or sent."
                action={
                  hasB ? (
                    <Tabs value={read} onValueChange={(v) => setReading(v as "a" | "b")}>
                      <TabsList aria-label="Which version to read">
                        <TabsTrigger value="a">Version A</TabsTrigger>
                        <TabsTrigger value="b">Version B</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  ) : undefined
                }
              >
                <DraftPreview script={read === "b" ? values.script_b : values.script} language={values.language} />
              </Field>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {message ? "Save changes" : "Add message"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One of a greeting's texts: the greeting itself, or its version B. Each has its own count and its
 * own Insert field menu, and remembers where its caret was when the menu was opened: a menu takes
 * the focus, and the field goes where the admin was typing in this text, not at its end and not
 * into the other one.
 */
function GreetingText({
  id, label, required, error, hint, placeholder, rows, value, onChange, registration, fields,
}: {
  id: string;
  label: string;
  required?: boolean;
  error: string | undefined;
  hint: React.ReactNode;
  placeholder: string;
  rows: number;
  value: string;
  onChange: (next: string) => void;
  registration: UseFormRegisterReturn;
  fields: readonly GreetingField[] | undefined;
}) {
  const { ref: register, ...input } = registration;
  const text = React.useRef<HTMLTextAreaElement | null>(null);
  const caret = React.useRef<[number, number] | null>(null);
  const keepCaret = () => {
    const el = text.current;
    if (el) caret.current = [el.selectionStart, el.selectionEnd];
  };
  const putField = (field: GreetingField) => {
    const [start, end] = caret.current ?? [value.length, value.length];
    const next = insertField(value, field.name, start, end);
    onChange(next.script);
    caret.current = [next.caret, next.caret];
  };
  /** Back to the text, with the caret after the field that was put in, so the typing goes on. */
  const backToText = () => {
    const el = text.current;
    if (!el || !caret.current) return;
    el.focus();
    el.setSelectionRange(caret.current[0], caret.current[1]);
  };
  return (
    <Field
      label={label}
      htmlFor={id}
      required={required}
      error={error}
      hint={hint}
      action={
        <span className="flex items-center gap-2">
          <span
            className={cn("text-xs tabular-nums", value.length > SCRIPT_MAX ? "text-danger-11" : "text-text-subtle")}
          >
            {value.length} of {SCRIPT_MAX}
          </span>
          <InsertField fields={fields} onOpen={keepCaret} onPick={putField} onPicked={backToText} />
        </span>
      }
    >
      <Textarea
        rows={rows}
        spellCheck
        className={cn("font-sans text-sm leading-relaxed", rows > 5 ? "min-h-36" : "min-h-24")}
        placeholder={placeholder}
        invalid={Boolean(error)}
        {...input}
        ref={(el: HTMLTextAreaElement | null) => {
          register(el);
          text.current = el;
        }}
        onBlur={(e) => {
          keepCaret();
          void input.onBlur(e);
        }}
      />
    </Field>
  );
}

/**
 * The fields a greeting can use, by what they are about. Picking one writes it into the greeting
 * where the caret was (`onOpen` notes that before the menu takes the focus), and the focus then
 * goes to the greeting rather than back to this button (`onPicked`), which is where a menu puts
 * it when it closes. A menu closed without a pick leaves the focus on the button, as any menu does.
 */
function InsertField({
  fields, onOpen, onPick, onPicked,
}: {
  fields: readonly GreetingField[] | undefined;
  onOpen: () => void;
  onPick: (field: GreetingField) => void;
  onPicked: () => void;
}) {
  const groups = [...new Set((fields ?? []).map((f) => f.group))];
  const picked = React.useRef(false);
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) return;
        picked.current = false;
        onOpen();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={!fields || fields.length === 0}>
          <Braces />
          Insert field
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-80 w-72 overflow-y-auto"
        onCloseAutoFocus={(e) => {
          if (!picked.current) return;
          e.preventDefault();
          onPicked();
        }}
      >
        {groups.map((group) => (
          <React.Fragment key={group}>
            <DropdownMenuLabel>{group}</DropdownMenuLabel>
            {(fields ?? [])
              .filter((f) => f.group === group)
              .map((f) => (
                <DropdownMenuItem
                  key={f.name}
                  onSelect={() => {
                    picked.current = true;
                    onPick(f);
                  }}
                  className="flex-col items-start gap-0"
                >
                  <span>{f.label}</span>
                  <span className="text-xs text-text-muted">
                    {f.hint}
                    {f.example ? `. Said as “${f.example}”` : ""}
                  </span>
                </DropdownMenuItem>
              ))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
